import React, { useState } from "react";
import { Card } from "antd";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { US_STATES_GEOJSON, STATE_NAME_TO_POSTAL } from "../utils/utils";

/* -------------------- Types -------------------- */

export interface MarketDataNew {
  location: string;
  Investment: string;
  EstimatedInvestment: string;
  duration: string;
  Power: number;
  abs_lift_in_perc: string;
  PercentInvestment: string;
  resolvedState?: string;
}

interface HoverData extends MarketDataNew {
  x: number;
  y: number;
}

interface MarketAnalysisDashboardProps {
  data: any[];
  allLocationData: any[];
}

/* -------------------- Helpers -------------------- */

const parseInvestment = (value: string) =>
  Number(value?.replace(/[^0-9.]/g, "")) || 0;

const STATE_NAMES = Object.keys(STATE_NAME_TO_POSTAL);

const POSTAL_TO_STATE_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_NAME_TO_POSTAL).map(([name, abbr]) => [abbr, name])
);

const normalizeLocationKey = (value: string): string =>
  (value || "").trim().toLowerCase().replace(/\s+/g, " ");

const expandCityState = (value: string): string => {
  const [city, stateToken] = normalizeLocationKey(value).split(",").map((p) => p.trim());
  if (!city || !stateToken) return normalizeLocationKey(value);
  const fullState =
    stateToken.length === 2
      ? POSTAL_TO_STATE_NAME[stateToken.toUpperCase()] || stateToken
      : stateToken;
  return `${city},${fullState.toLowerCase()}`;
};

// Every market gets shown as its whole state, the same way the Upload
// step's map does - never a pin at an exact city. A market's raw location
// can already be a state, or it can be "City, ST" / a DMA code, in which
// case we pull the state out of its geocoded address instead.
const resolveStateName = (location: string, geocodedAddress?: string): string | null => {
  const loc = (location || "").trim();

  const directMatch = STATE_NAMES.find((s) => s.toLowerCase() === loc.toLowerCase());
  if (directMatch) return directMatch;

  if (loc.length === 2 && POSTAL_TO_STATE_NAME[loc.toUpperCase()]) {
    return POSTAL_TO_STATE_NAME[loc.toUpperCase()];
  }

  if (!geocodedAddress) return null;

  for (const part of geocodedAddress.split(",").map((p) => p.trim())) {
    if (part.length === 2 && POSTAL_TO_STATE_NAME[part.toUpperCase()]) {
      return POSTAL_TO_STATE_NAME[part.toUpperCase()];
    }
    const found = STATE_NAMES.find((s) => s.toLowerCase() === part.toLowerCase());
    if (found) return found;
  }

  return null;
};

/* -------------------- Component -------------------- */

const MarketAnalysisDashboard: React.FC<MarketAnalysisDashboardProps> = ({
  data,
  allLocationData = [],
}) => {
  const [hovered, setHovered] = useState<HoverData | null>(null);

  const handleHover = (e: React.MouseEvent<SVGElement, MouseEvent>, item: MarketDataNew) => {
    setHovered({ ...item, x: e.clientX, y: e.clientY });
  };

  /* ---------- Transform Data ---------- */

  const transformedData: MarketDataNew[] = data.map((item) => {
    const itemKey = normalizeLocationKey(item.location);
    let match = allLocationData.find(
      (coords) =>
        coords.locationOriginal &&
        normalizeLocationKey(coords.locationOriginal) === itemKey
    );

    if (!match) {
      const fallbackKey = expandCityState(item.location);
      match = allLocationData.find(
        (coords) =>
          coords.locationOriginal &&
          expandCityState(coords.locationOriginal) === fallbackKey
      );
    }

    return {
      ...item,
      resolvedState: resolveStateName(item.location, match?.locationAddress),
    };
  });

  const stateData = transformedData.filter((d) => d.resolvedState);

  /* -------------------- Render -------------------- */

  return (
    <div className="flex flex-col gap-4 w-full h-full mt-5">
      <Card
        className="flex-1 shadow-xl rounded-lg"
        bodyStyle={{
          height: "70vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <ComposableMap
          projection="geoAlbersUsa"
          width={900}
          height={600}
          style={{ width: "100%", height: "auto" }}
        >
          <ZoomableGroup>
            <Geographies geography={US_STATES_GEOJSON}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateName = geo.properties.name;

                  const state = stateData.find(
                    (s) => s.resolvedState!.toLowerCase() === stateName.toLowerCase()
                  );

                  const investment = state
                    ? parseInvestment(state.Investment)
                    : 0;

                  const fillColor =
                    investment > 300
                      ? "#1e3a8a"
                      : investment > 200
                      ? "#1d4ed8"
                      : investment > 100
                      ? "#3b82f6"
                      : investment > 0
                      ? "#93c5fd"
                      : "#f1f5f9";

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fillColor}
                      stroke="#ffffff"
                      strokeWidth={0.6}
                      onMouseEnter={(e) => state && handleHover(e, state)}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          outline: "none",
                          filter: "brightness(0.95)",
                          cursor: state ? "pointer" : "default",
                        },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* ---------- Tooltip ---------- */}
        {hovered && (
          <div
            style={{
              position: "fixed",
              top: hovered.y + 12,
              left: hovered.x + 12,
              zIndex: 9999,
            }}
            className="bg-white rounded-2xl shadow-xl border border-slate-100 w-[300px]"
          >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b">
              <h3 className="text-lg font-bold text-slate-800">
                {hovered.location}
              </h3>
              <div className="text-xs text-slate-500 mt-1">State Overview</div>
            </div>

            <div className="p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span>⚡ Power</span>
                <strong>{hovered.Power}</strong>
              </div>

              <div className="flex justify-between">
                <span>📈 Abs Lift</span>
                <strong className="text-green-600">
                  +{hovered.abs_lift_in_perc}
                </strong>
              </div>

              <div className="flex justify-between">
                <span>📊 % Investment</span>
                <strong>{hovered.PercentInvestment}</strong>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MarketAnalysisDashboard;
